/**
 * Initializes Google Tag Manager (GTM)
 * This function should be called once when the app loads
 * Supports both GTM containers (GTM-XXXXXX) and GA4 measurement IDs (G-XXXXXX)
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    google_tag_manager?: unknown;
  }
}

let analyticsInitializationQueued = false;

export const initializeGoogleAnalytics = (
  TAG_ID: string,
  options: { synchronous?: boolean } = {}
): void => {
  if (typeof window === 'undefined') return;

  // Check if GTM/Analytics is already loaded
  if (window.dataLayer && window.google_tag_manager) return;
  if (analyticsInitializationQueued) return;
  analyticsInitializationQueued = true;

  const isGTM = TAG_ID.startsWith('GTM-');
  const isGA4 = TAG_ID.startsWith('G-');

  const loadAnalytics = (synchronous = false) => {
    if (isGTM) {
      // Official Google Tag Manager implementation
      // Initialize dataLayer before GTM script loads
      const dataLayerScript = document.createElement('script');
      dataLayerScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({'gtm.start': new Date().getTime(), event: 'gtm.js'});
      `;
      document.head.appendChild(dataLayerScript);

      // Load GTM container script
      const gtmScript = document.createElement('script');
      if (!synchronous) {
        gtmScript.async = true;
      }
      gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${TAG_ID}`;
      document.head.appendChild(gtmScript);

      // Add noscript fallback iframe to body (for users with JS disabled)
      addGTMNoScript(TAG_ID);
    } else if (isGA4) {
      // Load GA4 with gtag.js (legacy support)
      const script1 = document.createElement('script');
      if (!synchronous) {
        script1.async = true;
        script1.defer = true;
      }
      script1.src = `https://www.googletagmanager.com/gtag/js?id=${TAG_ID}`;
      document.head.appendChild(script1);

      // Initialize gtag
      const script2 = document.createElement('script');
      script2.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(): any {dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${TAG_ID}', {
          'send_page_view': true,
          'anonymize_ip': true,
          'cookie_flags': 'SameSite=None;Secure'
        });
      `;
      document.head.appendChild(script2);
    } else {
      console.warn('Invalid tag ID format. Expected GTM-XXXXXX or G-XXXXXX, got:', TAG_ID);
    }
  };

  // Helper function to add GTM noscript fallback
  const addGTMNoScript = (gtmId: string) => {
    // Check if noscript already exists to avoid duplicates
    const existingNoscript = document.querySelector('noscript[data-gtm]');
    if (existingNoscript) return;

    const noscript = document.createElement('noscript');
    noscript.setAttribute('data-gtm', 'true');
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    // Insert at the beginning of body as per GTM documentation
    document.body.insertBefore(noscript, document.body.firstChild);
  };

  // If synchronous loading is requested, load immediately
  if (options.synchronous) {
    loadAnalytics(true);
    return;
  }

  // Defer analytics loading until user interaction or after significant delay
  // This prevents blocking initial page load and improves performance metrics
  let analyticsLoaded = false;

  const loadAnalyticsOnce = () => {
    if (analyticsLoaded) return;
    analyticsLoaded = true;
    loadAnalytics();
  };

  // Load on user interaction (scroll, click, or touch)
  const interactionEvents = ['scroll', 'mousedown', 'touchstart', 'keydown'];
  for (const event of interactionEvents) {
    window.addEventListener(event, loadAnalyticsOnce, {
      once: true,
      passive: true,
    });
  }

  const scheduleFallback = () => {
    if (analyticsLoaded) return;
    // Use requestIdleCallback for better performance, fallback to longer setTimeout
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(loadAnalyticsOnce, { timeout: 5000 });
    } else {
      // Use longer delay to avoid blocking main thread
      setTimeout(loadAnalyticsOnce, 5000);
    }
  };

  if (document.readyState === 'complete') {
    scheduleFallback();
  } else {
    window.addEventListener('load', scheduleFallback, { once: true });
  }
};

/**
 * Track events with GTM/GA4
 * Works with both Google Tag Manager (via dataLayer) and GA4 (via gtag)
 * @param {string} eventName - The name of the event to track
 * @param {object} eventParams - Parameters to send with the event
 */
export const trackEvent = (eventName: string, eventParams = {}): void => {
  if (typeof window === 'undefined') return;

  // GTM method (preferred if available)
  if (window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      ...eventParams,
    });
    return;
  }

  // Fallback to gtag if available (for GA4 direct implementation)
  if (window.gtag) {
    window.gtag('event', eventName, eventParams);
    return;
  }

  console.warn('Analytics not initialized. Event not tracked:', eventName);
};

/**
 * Track page views with GTM/GA4
 * @param {string} pagePath - The page path to track
 * @param {string} pageTitle - The page title
 */
export const trackPageView = (pagePath: string, pageTitle: string): void => {
  if (typeof window === 'undefined') return;

  // GTM method
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'page_view',
      page_path: pagePath,
      page_title: pageTitle,
    });
    return;
  }

  // Fallback to gtag
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: pageTitle,
    });
    return;
  }
};
